import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { toast } from 'react-hot-toast';
import { Icons } from '../lib/Icons';

const ProgressReportGenerator = ({ studentId, studentName, progressData, onGenerateReport }) => {
  const [reportType, setReportType] = useState('comprehensive');
  const [includeCharts, setIncludeCharts] = useState(true);
  const [generating, setGenerating] = useState(false);

  const generateReport = async () => {
    try {
      setGenerating(true);
      
      // Create report content
      const reportContent = createReportContent();
      
      // Generate PDF using html2pdf
      const element = document.createElement('div');
      element.innerHTML = reportContent;
      element.style.padding = '20px';
      element.style.fontFamily = 'Arial, sans-serif';
      
      const opt = {
        margin: 1,
        filename: `${studentName}_Progress_Report_${new Date().toLocaleDateString()}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
      };
      
      // Import html2pdf dynamically to avoid SSR issues
      const html2pdf = (await import('html2pdf.js')).default;
      await html2pdf().from(element).set(opt).save();
      
      toast.success('Progress report generated successfully!');
      
      if (onGenerateReport) {
        onGenerateReport(reportContent);
      }
      
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate progress report');
    } finally {
      setGenerating(false);
    }
  };

  const createReportContent = () => {
    if (!progressData) return '<p>No progress data available</p>';
    
    const currentDate = new Date().toLocaleDateString();
    const displayName = studentName || progressData.studentName || 'Unknown Student';
    
    let content = `
      <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto;">
        <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="color: #1f2937; margin-bottom: 10px;">Student Progress Report</h1>
          <h2 style="color: #374151; margin-bottom: 5px;">${displayName}</h2>
          <p style="color: #6b7280;">Generated on: ${currentDate}</p>
        </div>
        
        <div style="margin-bottom: 30px;">
          <h3 style="color: #1f2937; border-bottom: 2px solid #e5e7eb; padding-bottom: 10px;">
            <Icons.ChartBar className="w-5 h-5 mr-2 inline-block" /> Overall Progress Summary
          </h3>
          <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-top: 20px;">
            <div style="text-align: center; padding: 20px; background-color: #f3f4f6; border-radius: 8px;">
              <div style="font-size: 24px; font-weight: bold; color: #3b82f6;">
                ${progressData.overallProgress?.percentageComplete || 0}%
              </div>
              <div style="color: #6b7280; font-size: 14px;">Overall Progress</div>
            </div>
            <div style="text-align: center; padding: 20px; background-color: #f3f4f6; border-radius: 8px;">
              <div style="font-size: 24px; font-weight: bold; color: #10b981;">
                ${progressData.curriculumProgress?.percentageComplete || 0}%
              </div>
              <div style="color: #6b7280; font-size: 14px;">Curriculum Progress</div>
            </div>
            <div style="text-align: center; padding: 20px; background-color: #f3f4f6; border-radius: 8px;">
              <div style="font-size: 24px; font-weight: bold; color: #8b5cf6;">
                ${progressData.interventionProgress?.attendanceRate || 0}%
              </div>
              <div style="color: #6b7280; font-size: 14px;">Attendance Rate</div>
            </div>
          </div>
        </div>
        
        <div style="margin-bottom: 30px;">
          <h3 style="color: #1f2937; border-bottom: 2px solid #e5e7eb; padding-bottom: 10px;">
            <Icons.BookOpen className="w-5 h-5 mr-2 inline-block" /> Curriculum Progress
          </h3>
          <p style="margin-bottom: 15px;">
            <strong>Targets Completed:</strong> ${progressData.curriculumProgress?.completedTargets || 0} / ${progressData.curriculumProgress?.totalTargets || 0}
          </p>
          <div style="margin-top: 20px;">
            <h4 style="color: #374151; margin-bottom: 15px;">Learning Targets:</h4>
            <div style="margin-left: 20px;">
    `;
    
    if (progressData.curriculumProgress?.targets && progressData.curriculumProgress.targets.length > 0) {
      progressData.curriculumProgress.targets.forEach((target, index) => {
        content += `
          <div style="margin-bottom: 10px; padding: 10px; background-color: #f9fafb; border-radius: 4px;">
            <p style="margin: 0; font-weight: 500;">${index + 1}. ${target.target}</p>
            <p style="margin: 5px 0 0 0; font-size: 14px; color: #6b7280;">
              Status: ${target.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </p>
            ${target.notes ? `<p style="margin: 5px 0 0 0; font-size: 14px; color: #6b7280;">Notes: ${target.notes}</p>` : ''}
          </div>
        `;
      });
    } else {
      content += '<p style="color: #6b7280; font-style: italic;">No learning targets available</p>';
    }
    
    content += `
            </div>
          </div>
        </div>
        
        <div style="margin-bottom: 30px;">
          <h3 style="color: #1f2937; border-bottom: 2px solid #e5e7eb; padding-bottom: 10px;">
            <Icons.Target className="w-5 h-5 mr-2 inline-block" /> Intervention Progress
          </h3>
          <p style="margin-bottom: 15px;">
            <strong>Total Sessions:</strong> ${progressData.interventionProgress?.totalSessions || 0}<br>
            <strong>Sessions Attended:</strong> ${progressData.interventionProgress?.attendedSessions || 0}<br>
            <strong>Attendance Rate:</strong> ${progressData.interventionProgress?.attendanceRate || 0}%
          </p>
          
          ${progressData.interventionProgress?.progressNotes && progressData.interventionProgress.progressNotes.length > 0 ? `
          <div style="margin-top: 20px;">
            <h4 style="color: #374151; margin-bottom: 15px;">Recent Progress Notes:</h4>
            ${progressData.interventionProgress.progressNotes.map((note, index) => `
              <div style="margin-bottom: 15px; padding: 15px; background-color: #f0f9ff; border-left: 4px solid #3b82f6; border-radius: 4px;">
                <p style="margin: 0 0 5px 0; font-weight: 500; color: #1e40af;">${note.interventionName}</p>
                <p style="margin: 0 0 5px 0; color: #374151;">${note.note}</p>
                <p style="margin: 0; font-size: 12px; color: #6b7280;">${new Date(note.date).toLocaleDateString()}</p>
              </div>
            `).join('')}
          </div>
          ` : ''}

          ${progressData.interventionProgress?.targetAchievementSummary && progressData.interventionProgress.targetAchievementSummary.length > 0 ? `
          <div style="margin-top: 20px;">
            <h4 style="color: #374151; margin-bottom: 10px;">Target Achievement Summary</h4>
            ${progressData.interventionProgress.targetAchievementSummary.map((entry) => `
              <div style="margin-bottom: 12px; padding: 12px; background-color: #f9fafb; border-radius: 6px;">
                <p style="margin: 0 0 8px 0; font-weight: 600; color: #111827;">${entry.interventionName}</p>
                ${entry.targets.map(t => `
                  <div style=\"display: flex; justify-content: space-between; align-items: center; padding: 6px 8px; background: #ffffff; border: 1px solid #e5e7eb; border-radius: 4px; margin-bottom: 6px;\">\n
                    <span style=\"color: #374151; font-size: 14px;\">${t.targetText}</span>\n
                    <span style=\"color: #6b7280; font-size: 12px;\">Achieved: ${t.achievedSessions}/${t.totalScoredSessions} (${t.achievementRate}%)</span>
                  </div>
                `).join('')}
              </div>
            `).join('')}
          </div>
          ` : ''}
        </div>
        
        <div style="margin-bottom: 30px;">
          <h3 style="color: #1f2937; border-bottom: 2px solid #e5e7eb; padding-bottom: 10px;">
            <Icons.ArrowTrendingUp className="w-5 h-5 mr-2 inline-block" /> Progress Trends
          </h3>
          <p style="color: #6b7280;">
            <strong>Current Trend:</strong> ${progressData.overallProgress?.trend === 'improving' ? '↗ Improving' : 
                                            progressData.overallProgress?.trend === 'declining' ? '↘ Declining' : 
                                            '→ Stable'}
          </p>
          <p style="color: #6b7280;">
            <strong>Last Updated:</strong> ${new Date(progressData.overallProgress?.lastUpdated || new Date()).toLocaleDateString()}
          </p>
        </div>
        
        <div style="margin-top: 40px; padding: 20px; background-color: #f9fafb; border-radius: 8px; text-align: center;">
          <p style="color: #6b7280; font-size: 14px; margin: 0;">
            This report was automatically generated by the SEN Curriculum Management System.<br>
            For questions or concerns, please contact the SENCO team.
          </p>
        </div>
      </div>
    `;
    
    return content;
  };

  if (!progressData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle><Icons.Document className="w-5 h-5 mr-2 inline-block" /> Progress Report Generator</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            <p>No progress data available to generate report</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle><Icons.Document className="w-5 h-5 mr-2 inline-block" /> Progress Report Generator</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Report Type
            </label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="comprehensive">Comprehensive Report</option>
              <option value="curriculum">Curriculum Focus</option>
              <option value="intervention">Intervention Focus</option>
              <option value="summary">Executive Summary</option>
            </select>
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="includeCharts"
              checked={includeCharts}
              onChange={(e) => setIncludeCharts(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="includeCharts" className="ml-2 block text-sm text-gray-900">
              Include visual charts and graphs
            </label>
          </div>
          
          <div className="pt-4">
            <button
              onClick={generateReport}
              disabled={generating}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {generating ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Generating Report...
                </div>
              ) : (
                'Generate Progress Report'
              )}
            </button>
          </div>
          
          <div className="text-sm text-gray-600 text-center">
            <p>This will generate a comprehensive PDF report of {studentName}'s progress</p>
            <p>including curriculum targets, intervention attendance, and progress trends.</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProgressReportGenerator; 
